import {
  ChefHat,
  PaintRoller,
  Layers,
  Home,
  PanelsTopLeft,
  Squircle,
  Ruler,
} from "lucide-react";

export const COMPANY = {
  name: "Almeida's Home Services LLC",
  shortName: "Almeida's Home Services",
  phone: "(704) 390-4489",
  phoneRaw: "+17043904489",
  email: "sales@almeidashomeservices.com",
  website: "almeidashomeservices.com",
  address: "12521 Druids Glen Dr, Pineville, NC 28134",
  hours: "Monday–Saturday, 10 AM – 10 PM",
  serviceArea: "North & South Carolina",
  slogan: "Transforming Spaces, Building Dreams",
  campaign: "More Than Building, We Transform.",
  b2bTagline: "One Partner. Multiple Trades. Better Results.",
  instagram: "https://www.instagram.com/almeidashomeserv/",
  facebook: "https://www.facebook.com/almeidashomeserv",
};

const IMG = {
  kitchen:
    "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  bath:
    "https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  exterior:
    "https://images.unsplash.com/photo-1628744448840-55bdb2497bd4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  roofing:
    "https://images.unsplash.com/photo-1635424824849-1b09bdcc55b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  flooring:
    "https://images.unsplash.com/photo-1656122381069-9ec666d95cf1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  countertops:
    "https://images.unsplash.com/photo-1616596612351-5a7ae04e2840?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  blueprint:
    "https://images.unsplash.com/photo-1608303588026-884930af2559?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

// Home service cards — `slug` links each card to its dedicated page.
export const SERVICES = [
  {
    icon: ChefHat,
    slug: "kitchen-bath-remodeling",
    title: "Kitchen & Bath Remodel",
    desc: "Full renovations that reimagine the rooms you live in most — from layout to finishing touches.",
    alt: "Open-concept kitchen remodel with quartz island in Charlotte NC",
  },
  {
    icon: PaintRoller,
    slug: "drywall-painting",
    title: "Drywall & Painting",
    desc: "Flawless interior & exterior surfaces. Repairs, texture, and rich, durable color throughout.",
    alt: "Freshly painted interior with smooth drywall finish",
  },
  {
    icon: Layers,
    slug: "flooring",
    title: "Flooring",
    desc: "LVP, hardwood, laminate, tile & carpet — precisely installed to last for decades.",
    alt: "Hardwood flooring installation in a living room",
  },
  {
    icon: Home,
    slug: "roofing",
    title: "Roofing",
    desc: "Installation, repair, and storm-damage restoration that protects everything beneath it.",
    alt: "New asphalt shingle roof installation in the Charlotte metro",
  },
  {
    icon: PanelsTopLeft,
    slug: "siding",
    title: "Siding",
    desc: "Vinyl, fiber cement & wood siding that transforms curb appeal and shields your home.",
    alt: "Modern home exterior with new fiber cement siding",
  },
  {
    icon: Squircle,
    slug: "cabinets-countertops",
    title: "Cabinets & Countertops",
    tag: "Quartz & Granite",
    desc: "Quartz, granite & marble — plus wholesale supply for builders and contractors.",
    alt: "Quartz kitchen countertop with custom cabinets",
  },
  {
    icon: Ruler,
    slug: "design-project-management",
    title: "Design & Project Management",
    desc: "Interior design and hands-on management so your project runs smoothly, start to finish.",
    alt: "Remodel design plans and material samples on a table",
  },
];

export const TRUST = [
  { title: "Licensed & Insured", desc: "Fully covered, fully compliant." },
  { title: "Local & Family-Owned", desc: "Rooted in the Charlotte metro." },
  { title: "Experienced Crew", desc: "Seasoned trades on every job." },
  { title: "Fast Response", desc: "Quick quotes, quicker starts." },
];

export const DIFFERENTIATORS = [
  "Licensed & Insured",
  "Local & Family-Owned",
  "Experienced Crew",
  "Fast Response",
  "Great Value, Every Job",
  "Work Guaranteed",
  "100% Free, No-Obligation Estimate",
];

export const PORTFOLIO = [
  { category: "Residential", caption: "Open-Concept Kitchen", tag: "Kitchen", url: IMG.kitchen, alt: "Open-concept kitchen remodel with quartz island in Charlotte NC" },
  { category: "Residential", caption: "Spa Bathroom Retreat", tag: "Bath", url: IMG.bath, alt: "Remodeled spa bathroom with walk-in tile shower" },
  { category: "Commercial", caption: "Modern Exterior Build", tag: "Exterior", url: IMG.exterior, alt: "Modern home exterior with new siding and trim" },
  { category: "Residential", caption: "Storm-Ready Roofing", tag: "Roofing", url: IMG.roofing, alt: "New asphalt shingle roof after storm-damage replacement" },
  { category: "Residential", caption: "Warm Hardwood Living", tag: "Flooring", url: IMG.flooring, alt: "Warm hardwood flooring in a bright living room" },
  { category: "Commercial", caption: "Quartz & Granite Countertop Supply", tag: "Quartz & Granite", url: IMG.countertops, alt: "Quartz and granite countertop slabs for supply" },
];

// NOTE: Keep ONLY real customer testimonials here. Replace/extend with real Google reviews when available.
export const TESTIMONIALS = [
  {
    quote:
      "Absolutely the best professional in town! Super detail-oriented, full of great ideas, and always delivers top-quality work.",
    name: "Diana Calvo",
    role: "Homeowner, Charlotte NC",
  },
];

export const SERVICE_OPTIONS = [
  "Kitchen & Bath Remodel",
  "Drywall & Painting",
  "Flooring",
  "Roofing",
  "Siding",
  "Cabinets & Countertops",
  "Design & Project Management",
  "Builder Partnership",
  "Other",
];

export const IMAGES = {
  hero: IMG.kitchen,
  blueprint: IMG.blueprint,
  about: IMG.exterior,
};

// ---- Dedicated service pages ----
export const SERVICE_PAGES = [
  {
    slug: "kitchen-bath-remodeling",
    nav: "Kitchen & Bath",
    title: "Kitchen & Bathroom Remodeling in Charlotte, NC | Almeida's",
    meta: "Kitchen and bathroom remodels in Charlotte, Pineville & Matthews. Layout, cabinets, quartz, tile and lighting by one licensed, insured team. Free estimate.",
    h1: "Kitchen & Bathroom Remodeling in Charlotte, NC",
    serviceType: "Kitchen and Bathroom Remodeling",
    schemaName: "Kitchen & Bathroom Remodeling in Charlotte, NC",
    image: IMG.kitchen,
    alt: "Open-concept kitchen remodel with quartz island in Charlotte NC",
    body: "Your kitchen and bathrooms work harder than any other rooms in the house, so we plan them around how your family actually lives. Almeida's handles the whole job with one crew and one point of contact: demolition, plumbing and electrical coordination, drywall, tile, cabinets, quartz or granite countertops, lighting and final paint. Because we supply countertops directly, you choose from a wide selection of quartz and granite without paying showroom markups. Typical projects include open-concept kitchens, island additions, tub-to-shower conversions, walk-in showers and full primary-bath remodels. You get a clear written quote before work starts and a schedule you can count on.",
    h2s: ["What's included", "Our process", "Recent kitchen & bath projects", "Request your free estimate"],
  },
  {
    slug: "roofing",
    nav: "Roofing",
    title: "Roof Replacement & Repair in Charlotte, NC | Almeida's",
    meta: "Roof replacement, repair and storm-damage restoration in Charlotte, Pineville & Matthews. Licensed & insured. Free roof inspection and estimate.",
    h1: "Roof Replacement & Repair in Charlotte, NC",
    serviceType: "Roofing",
    schemaName: "Roof Replacement & Repair in Charlotte, NC",
    image: IMG.roofing,
    alt: "New asphalt shingle roof installation in the Charlotte metro",
    body: "Carolina summers and spring storms are hard on shingles. We inspect, repair and replace residential and light-commercial roofs across the Charlotte metro, from a few lifted shingles to a full tear-off with new underlayment, flashing and ventilation. After a storm, we document the damage with photos so you have what you need for your insurance claim. Every roof is cleaned up at the end of each day, and nails are swept from the driveway and yard before we leave.",
    h2s: ["Roof replacement", "Roof repair", "Storm & hail damage", "Free roof inspection"],
  },
  {
    slug: "siding",
    nav: "Siding",
    title: "Siding Installation & Repair in Charlotte, NC | Almeida's",
    meta: "Vinyl, fiber cement and wood siding installation and repair in Charlotte, Pineville & Matthews. Better curb appeal and protection. Free estimate.",
    h1: "Siding Installation & Repair in Charlotte, NC",
    serviceType: "Siding",
    schemaName: "Siding Installation & Repair in Charlotte, NC",
    image: IMG.exterior,
    alt: "Modern home exterior with new fiber cement siding",
    body: "New siding is one of the fastest ways to change how a home looks from the street, and it keeps moisture out of the walls. We install and repair vinyl, fiber cement and wood siding, including trim, soffit and fascia. We'll walk you through the trade-offs in cost, maintenance and lifespan for each material, then match colors and profiles to your home and neighborhood.",
    h2s: ["Vinyl siding", "Fiber cement siding", "Wood siding & trim", "Siding repair"],
  },
  {
    slug: "flooring",
    nav: "Flooring",
    title: "Flooring Installation in Charlotte, NC: LVP, Hardwood & Tile | Almeida's",
    meta: "LVP, hardwood, laminate, tile and carpet installation in Charlotte, Pineville & Matthews. Subfloor prep done right. Licensed & insured. Free estimate.",
    h1: "Flooring Installation in Charlotte, NC",
    serviceType: "Flooring Installation",
    schemaName: "Flooring Installation in Charlotte, NC",
    image: IMG.flooring,
    alt: "Hardwood flooring installation in a living room in Charlotte NC",
    body: "A floor only lasts as long as the prep under it. We level and repair subfloors before installing luxury vinyl plank, hardwood, laminate, tile or carpet, so you don't end up with gaps, squeaks or cracked grout later. We handle furniture moving, removal and disposal of the old flooring, transitions between rooms, baseboards and stairs, and we can coordinate the floors with a kitchen, bath or painting project in the same visit.",
    h2s: ["Luxury vinyl plank (LVP)", "Hardwood", "Tile", "Laminate & carpet", "Stairs"],
  },
  {
    slug: "drywall-painting",
    nav: "Drywall & Painting",
    title: "Drywall Repair & Painting in Charlotte, NC | Almeida's",
    meta: "Drywall installation, repair, texture and interior/exterior painting in Charlotte, Pineville & Matthews. Clean, on-schedule work. Free estimate.",
    h1: "Drywall & Painting in Charlotte, NC",
    serviceType: "Drywall and Painting",
    schemaName: "Drywall & Painting in Charlotte, NC",
    image: IMG.blueprint,
    alt: "Interior wall being painted after drywall finishing",
    body: "From patching a water-damaged ceiling to finishing a basement or repainting the whole house, we deliver smooth walls and clean lines. Our crew hangs, tapes and finishes drywall, matches existing textures, and paints interiors and exteriors with durable, washable finishes. We protect your floors and furniture, keep the work area tidy, and walk through the result with you before we call it done.",
    h2s: ["Drywall installation", "Drywall repair & texture", "Interior painting", "Exterior painting"],
  },
  {
    slug: "cabinets-countertops",
    nav: "Cabinets & Countertops",
    title: "Cabinets & Quartz/Granite Countertops in Charlotte, NC | Almeida's",
    meta: "Cabinet installation and quartz, granite & marble countertops in Charlotte. Direct supply pricing for homeowners and builders. Free estimate.",
    h1: "Cabinets & Countertops in Charlotte, NC",
    serviceType: "Cabinets and Countertops",
    schemaName: "Cabinets & Countertops in Charlotte, NC",
    image: IMG.countertops,
    alt: "Quartz and granite kitchen countertops with custom cabinets",
    body: "We install kitchen and bath cabinets and supply quartz, granite and marble countertops directly, which keeps your price lower and your timeline shorter. We measure, template, fabricate, deliver and install, including undermount sinks, backsplashes and edge profiles. Builders and contractors can buy countertops at wholesale pricing through our Builder Partnership Program.",
    h2s: ["Quartz countertops", "Granite & marble", "Cabinet installation", "Wholesale for builders"],
  },
  {
    slug: "design-project-management",
    nav: "Design & Management",
    title: "Remodel Design & Project Management in Charlotte, NC | Almeida's",
    meta: "Interior design and hands-on project management for remodels in Charlotte, Pineville & Matthews. One point of contact for every trade.",
    h1: "Design & Project Management in Charlotte, NC",
    serviceType: "Design and Project Management",
    schemaName: "Design & Project Management in Charlotte, NC",
    image: IMG.blueprint,
    alt: "Remodel design plans and finish samples laid out on a table",
    body: "Most remodels go wrong in the gaps between trades. We plan the layout and finishes with you, set the schedule, order materials, and coordinate every trade, so you have one person to call from the first sketch to the final walkthrough. You'll get regular updates and photos while work is underway.",
    h2s: ["Design planning", "Material selection", "Trade coordination", "Progress updates"],
  },
];

// ---- Service-area (city) pages ----
export const SERVICE_AREAS = [
  {
    slug: "charlotte-nc",
    city: "Charlotte",
    state: "NC",
    stateName: "North Carolina",
    title: "Remodeling Contractor in Charlotte, NC | Almeida's Home Services",
    meta: "Kitchen, bath, roofing, siding and flooring contractor serving Charlotte, NC neighborhoods from South End to Ballantyne. Free estimates.",
    h1: "Remodeling Contractor in Charlotte, NC",
    body: "Charlotte has everything from 1920s bungalows to new-build townhomes, and each needs a different approach. In older homes we often find uneven subfloors, outdated wiring and plaster walls behind a kitchen or bath remodel. Newer homes usually call for upgrades like quartz countertops, LVP flooring and open-concept layouts. We work throughout Charlotte, including South Charlotte, Ballantyne, SouthPark, Myers Park, Steele Creek and University City.",
  },
  {
    slug: "pineville-nc",
    city: "Pineville",
    state: "NC",
    stateName: "North Carolina",
    title: "Remodeling Contractor in Pineville, NC | Almeida's Home Services",
    meta: "Pineville's local remodeling team: kitchens, baths, roofing, siding and flooring for homes and businesses in Pineville, NC. Free estimates.",
    h1: "Remodeling Contractor in Pineville, NC",
    body: "Pineville is just minutes from our Fort Mill base, so quick estimates and fast follow-ups are easy. We work on Pineville's established neighborhoods and on homes and businesses along the Polk Street and Carolina Place corridors, from storm-damaged roofs to full kitchen and bath remodels. Being local also means we know the area's permitting process and build to the standards our neighbors expect.",
  },
  {
    slug: "matthews-nc",
    city: "Matthews",
    state: "NC",
    stateName: "North Carolina",
    title: "Remodeling Contractor in Matthews, NC | Almeida's Home Services",
    meta: "Kitchen and bath remodels, flooring, roofing and siding for Matthews, NC homeowners near downtown and across Union County's edge. Free estimates.",
    h1: "Remodeling Contractor in Matthews, NC",
    body: "Many homes in Matthews were built in the 1980s through the 2000s, so original kitchens, builder-grade baths and aging roofs are now due for an update. We help Matthews homeowners near downtown, along Sardis and Weddington roads and toward Mint Hill and Stallings modernize without moving: new kitchens and baths, LVP or hardwood floors, fresh paint and replacement roofs and siding.",
  },
  {
    slug: "fort-mill-sc",
    city: "Fort Mill",
    state: "SC",
    stateName: "South Carolina",
    title: "Remodeling Contractor in Fort Mill, SC | Almeida's Home Services",
    meta: "Kitchen and bath remodels, flooring, roofing and siding for Fort Mill and Tega Cay, SC homes, just across the line from Charlotte. Free estimates.",
    h1: "Remodeling Contractor in Fort Mill, SC",
    body: "Fort Mill has grown fast, and many of its subdivisions are now 10 to 20 years old: the age when builder-grade kitchens, carpeted floors and original roofs start to need replacing. We work across Fort Mill, Tega Cay, Baxter Village and the Gold Hill Road area, right here at our home base. Most Fort Mill projects we see are kitchen refreshes with new quartz countertops, LVP throughout the main floor, primary-bath upgrades and storm-related roof and siding repairs.",
  },
  {
    slug: "rock-hill-sc",
    city: "Rock Hill",
    state: "SC",
    stateName: "South Carolina",
    title: "Remodeling Contractor in Rock Hill, SC | Almeida's Home Services",
    meta: "Remodeling, roofing, siding and flooring for homes and businesses in Rock Hill, SC. One licensed, insured team for every trade. Free estimates.",
    h1: "Remodeling Contractor in Rock Hill, SC",
    body: "Rock Hill mixes historic homes near downtown and Winthrop with newer neighborhoods off Celanese Road and India Hook. Older homes often need careful work around original trim, plaster and uneven floors, while newer homes are ready for kitchen, bath and flooring upgrades. We also serve Rock Hill businesses with commercial build-outs, flooring and countertop supply.",
  },
];

// Grouping for the hub page + footer. `extra` are plain-text (non-linked) nearby towns.
export const AREA_GROUPS = [
  { state: "North Carolina", cities: ["charlotte-nc", "pineville-nc", "matthews-nc"], extra: ["Mint Hill", "Indian Trail", "Huntersville", "Concord"] },
  { state: "South Carolina", cities: ["fort-mill-sc", "rock-hill-sc"], extra: ["Tega Cay", "Indian Land", "Lancaster"] },
];
